import { createFileRoute } from "@tanstack/react-router";
import XeroLogo from "@/components/svgs/xero.svg";
import QuickBooksLogo from "@/components/svgs/quickbook.svg";

// ─── Route types ──────────────────────────────────────────────────────────────
// "requests" & "signatures" are legacy deep-link aliases → kept for compat
type Tab =
  | "overview"
  | "insights"
  | "requests"
  | "signatures"
  | "documents"
  | "messages"
  | "activity";

type ClientSearch = {
  tab?: Tab;
  page?: number;
  limit?: number;
};

export const Route = createFileRoute("/_app/clients/$clientId")({
  validateSearch: (search: Record<string, unknown>): ClientSearch => {
    return {
      tab: (search.tab as Tab) || undefined,
      page: Number(search.page) || undefined,
      limit: Number(search.limit) || undefined,
    };
  },
  component: ClientDetailPage,
});

// ─── Imports ──────────────────────────────────────────────────────────────────
import { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { getSession } from "@/lib/session";
import { trpc } from "@/lib/trpc";
import { runtimeConfig } from "@/lib/runtime-config";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/toast";
import {
  PAGE_SHELL,
  CARD_SECTION,
  ACTION_BTN,
  GHOST_BTN,
  REQUEST_PILL,
  REQUEST_STATUS_LABEL,
  SIGNATURE_PILL,
  SIGNATURE_STATUS_LABEL,
  type RequestStatus,
  type SignatureStatus,
} from "@/lib/ui-tokens";
import {
  EditClientModal,
  NewRequestModal,
  NewSignatureModal,
} from "@/components/modals";
import { ClientIntegrationMapper } from "@/components/modals/ClientIntegrationMapper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DocumentViewer,
  ViewerShift,
  ViewButton,
  useDocumentViewer,
} from "@/components/modals/DocumentViewer";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_LIMITS = [10, 25, 50] as const;
type PageLimit = (typeof PAGE_LIMITS)[number];

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "insights", label: "Insights" },
  { id: "requests", label: "Requests" },
  { id: "signatures", label: "Signatures" },
  { id: "documents", label: "Documents" },
  { id: "messages", label: "Messages" },
  { id: "activity", label: "Activity" },
];

const ACTIVITY_BG: Record<string, string> = {
  doc_received: "bg-(--green-bg)          border-(--colors-green-200)",
  signature_completed: "bg-(--green-bg)          border-(--colors-green-200)",
  sig_signed: "bg-(--green-bg)          border-(--colors-green-200)",
  document_approved: "bg-(--green-bg)          border-(--colors-green-200)",
  status_updated: "bg-(--green-bg)          border-(--colors-green-200)",
  signature_declined: "bg-(--colors-red-100)    border-(--colors-red-200)",
  document_rejected: "bg-(--colors-red-100)    border-(--colors-red-200)",
  signature_requested: "bg-(--muted-token)       border-(--border)",
  request_created: "bg-(--muted-token)       border-(--border)",
  message: "bg-(--colors-blue-100)   border-(--colors-blue-200)",
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
function ActivityIcon({ type }: { type: string }) {
  if (
    [
      "doc_received",
      "signature_completed",
      "document_approved",
      "status_updated",
      "sig_signed",
    ].includes(type)
  )
    return (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path
          d="M2 5l2.5 2.5L8 2"
          stroke="var(--green)"
          strokeWidth="1.3"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
      </svg>
    );
  if (["signature_declined", "document_rejected"].includes(type))
    return (
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
        <path
          d="M2 2l8 8M10 2L2 10"
          stroke="var(--warn)"
          strokeWidth="1.3"
          strokeLinecap="square"
        />
      </svg>
    );
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <rect
        x="2"
        y="1"
        width="8"
        height="10"
        stroke="var(--muted)"
        strokeWidth="1.2"
      />
      <path
        d="M4 4h4M4 7h4"
        stroke="var(--muted)"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
    </svg>
  );
}

function formatBytes(value: number) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Reusable pagination bar */
function Pagination({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
}: {
  page: number;
  limit: PageLimit;
  total: number;
  onPageChange: (p: number) => void;
  onLimitChange: (l: PageLimit) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
      {/* Count + limit selector */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground">
          {total === 0 ? "0 items" : `${from}–${to} of ${total}`}
        </span>
        <span className="text-[10px] text-muted-foreground/40">·</span>
        <div className="flex items-center gap-1">
          {PAGE_LIMITS.map((l) => (
            <button
              key={l}
              onClick={() => {
                onLimitChange(l);
                onPageChange(1);
              }}
              className={[
                "h-5 min-w-[24px] px-1.5 text-[9px] font-bold uppercase transition-colors",
                limit === l
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground border border-(--border)",
              ].join(" ")}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Prev / Next */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="flex h-6 w-6 items-center justify-center border border-(--border) text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M6.5 2L3.5 5l3 3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="square"
            />
          </svg>
        </button>
        <span className="px-1.5 text-[10px] text-muted-foreground tabular-nums">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="flex h-6 w-6 items-center justify-center border border-(--border) text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M3.5 2L6.5 5l-3 3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="square"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/** Panel-level section header — label left, action right */
function PanelHeader({
  label,
  action,
}: {
  label: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
        {label}
      </p>
      {action}
    </div>
  );
}

/** Inline empty state */
function EmptyRow({ message }: { message: string }) {
  return (
    <div className="px-4 py-10 text-[11px] text-muted-foreground">
      {message}
    </div>
  );
}

/** New-request dropdown — preserved from original, tidied */
function NewRequestDropdown({
  onDocRequest,
  onSigRequest,
}: {
  onDocRequest: () => void;
  onSigRequest: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} className={ACTION_BTN}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M5 1v8M1 5h8"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="square"
          />
        </svg>
        <span>New request</span>
        <svg
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
          className="ml-0.5 opacity-50"
        >
          <path
            d="M2 3l2 2 2-2"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="square"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-60 border border-(--border) bg-card shadow-xl">
          {[
            {
              label: "Document request",
              sub: "Request files via magic link",
              icon: (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <rect
                    x="2"
                    y="1"
                    width="10"
                    height="12"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M4 5h6M4 8h6M4 11h4"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="square"
                  />
                </svg>
              ),
              onClick: () => {
                setOpen(false);
                onDocRequest();
              },
            },
            {
              label: "Signature request",
              sub: "Send a document for signing",
              icon: (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M3 10l2.5-2.5 2 2 4.5-5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  />
                  <path
                    d="M1 13h12"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="square"
                  />
                </svg>
              ),
              onClick: () => {
                setOpen(false);
                onSigRequest();
              },
            },
          ].map((item, i) => (
            <>
              {i > 0 && <div className="h-px bg-(--border)" />}
              <button
                key={item.label}
                onClick={item.onClick}
                className="flex w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-(--surface-hover) transition-colors"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-(--border) bg-(--muted-token)">
                  {item.icon}
                </span>
                <div>
                  <p className="text-[11px] font-semibold text-foreground">
                    {item.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {item.sub}
                  </p>
                </div>
              </button>
            </>
          ))}
        </div>
      )}
    </div>
  );
}
function IntegrationLogo({ name }: { name: string }) {
  const logos: Record<string, string> = {
    Xero: XeroLogo,

    QuickBooks: QuickBooksLogo,
  };

  const src = logos[name];

  if (!src) {
    return (
      <div className="w-4 h-4 flex items-center justify-center text-[10px] font-bold text-muted-foreground"></div>
    );
  }

  return (
    <div className="w-10 h-10 flex items-center justify-center shrink-0 p-2">
      <img
        src={src}
        alt={`${name} logo`}
        className={`max-w-full max-h-full object-contain ${name === "QuickBooks" && "dark:invert"}`}
      />
    </div>
  );
}

// ─── QBO Customer Panel ───────────────────────────────────────────────────────
interface QboMeta {
  Id?: string;
  DisplayName?: string;
  CompanyName?: string;
  Balance?: number;
  CurrencyRef?: { value: string; name?: string };
  PrimaryEmailAddr?: { Address?: string };
  PrimaryPhone?: { FreeFormNumber?: string };
  BillAddr?: {
    Line1?: string;
    City?: string;
    CountrySubDivisionCode?: string;
    PostalCode?: string;
    Country?: string;
  };
  MetaData?: { LastUpdatedTime?: string };
  Notes?: string;
}

function QboCustomerPanel({
  clientId,
  qboCustomerId,
  qboMetadataRaw,
  onUnlink,
  showSwitch,
  onSwitch,
}: {
  clientId: string;
  qboCustomerId: string;
  qboMetadataRaw: string | null | undefined;
  onUnlink: () => void;
  showSwitch: boolean;
  onSwitch: () => void;
}) {
  const utils = trpc.useUtils();
  const syncMutation = trpc.integrations.syncQboCustomer.useMutation({
    onSuccess: () => {
      toast.success("QBO data refreshed.");
      utils.clients.get.invalidate({ id: clientId });
    },
    onError: (err) => toast.error(err.message || "Failed to refresh QBO data."),
  });

  const meta: QboMeta | null = (() => {
    if (!qboMetadataRaw) return null;
    try {
      return JSON.parse(qboMetadataRaw) as QboMeta;
    } catch {
      return null;
    }
  })();

  const currency = meta?.CurrencyRef?.value ?? "USD";
  const lastSync = meta?.MetaData?.LastUpdatedTime
    ? new Date(meta.MetaData.LastUpdatedTime).toLocaleDateString()
    : null;

  const billAddr = [
    meta?.BillAddr?.Line1,
    meta?.BillAddr?.City,
    meta?.BillAddr?.CountrySubDivisionCode,
    meta?.BillAddr?.PostalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="border border-(--border) bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <IntegrationLogo name="QuickBooks" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
            QuickBooks Customer
          </p>
          {meta && (
            <span className="text-[9px] text-muted-foreground font-mono">
              #{qboCustomerId.substring(0, 8)}...
            </span>
          )}
        </div>
        <button
          onClick={() => syncMutation.mutate({ clientId })}
          disabled={syncMutation.isPending}
          className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2 disabled:opacity-40"
        >
          {syncMutation.isPending ? (
            <span className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            // <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            //   <path
            //     d="M10 6A4 4 0 1 1 6 2"
            //     stroke="currentColor"
            //     strokeWidth="1.4"
            //     strokeLinecap="square"
            //   />
            //   <path
            //     d="M6 0v4h4"
            //     stroke="currentColor"
            //     strokeWidth="1.4"
            //     strokeLinecap="square"
            //   />
            // </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
              stroke-linejoin="square"
            >
              <path d="M20 5V10H15" />
              <path d="M4 19V14H9" />
              <path d="M20 10C18.5 6.5 15.5 4 12 4C8 4 4.8 6.7 4 10" />
              <path d="M4 14C5.5 17.5 8.5 20 12 20C16 20 19.2 17.3 20 14" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {!meta ? (
        <div className="px-4 py-6 text-center">
          <p className="text-[11px] text-muted-foreground mb-3">
            No QBO data synced yet. Click{" "}
            <span className="font-semibold">Refresh</span> to load this
            customer's QuickBooks details.
          </p>
          <div className="flex items-center justify-center gap-3">
            {showSwitch && (
              <button
                onClick={onSwitch}
                className="text-[10px] font-semibold text-(--brand) hover:underline"
              >
                Switch to Xero
              </button>
            )}
            <button
              onClick={onUnlink}
              className="text-[10px] font-semibold text-(--warn) hover:underline"
            >
              Unlink
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4">
          {/* Balance + currency row */}
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-1 border border-(--border) px-3.5 py-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                Outstanding Balance
              </p>
              <p
                className={`text-[20px] font-bold leading-none tabular-nums ${
                  (meta.Balance ?? 0) > 0 ? "text-(--warn)" : "text-(--green)"
                }`}
              >
                {currency}{" "}
                {(meta.Balance ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <div className="flex-1 border border-(--border) px-3.5 py-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                QBO Name
              </p>
              <p className="text-[13px] font-semibold text-foreground truncate">
                {meta.DisplayName ?? "—"}
              </p>
              {meta.CompanyName && meta.CompanyName !== meta.DisplayName && (
                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                  {meta.CompanyName}
                </p>
              )}
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2">
            {meta.PrimaryEmailAddr?.Address && (
              <div className="border border-(--border) px-3 py-2.5">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Email (QBO)
                </p>
                <p className="text-[11px] text-foreground truncate">
                  {meta.PrimaryEmailAddr.Address}
                </p>
              </div>
            )}
            {meta.PrimaryPhone?.FreeFormNumber && (
              <div className="border border-(--border) px-3 py-2.5">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Phone (QBO)
                </p>
                <p className="text-[11px] text-foreground">
                  {meta.PrimaryPhone.FreeFormNumber}
                </p>
              </div>
            )}
            {billAddr && (
              <div className="border border-(--border) px-3 py-2.5 col-span-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Billing Address
                </p>
                <p className="text-[11px] text-foreground">{billAddr}</p>
              </div>
            )}
            {meta.Notes && (
              <div className="border border-(--border) px-3 py-2.5 col-span-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Notes (QBO)
                </p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {meta.Notes}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-(--border) flex items-center justify-between">
            {lastSync ? (
              <p className="text-[9.5px] text-muted-foreground/50">
                Last updated in QBO: {lastSync}
              </p>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              {showSwitch && (
                <button
                  onClick={onSwitch}
                  className="text-[10px] font-semibold text-(--brand) hover:underline"
                >
                  Switch to Xero
                </button>
              )}
              <button
                onClick={onUnlink}
                className="text-[10px] font-semibold text-(--warn) hover:underline"
              >
                Unlink
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Xero Contact Panel ───────────────────────────────────────────────────────
interface XeroMeta {
  ContactID?: string;
  Name?: string;
  EmailAddress?: string;
  Balances?: {
    AccountsReceivable?: {
      Outstanding?: number;
      Overdue?: number;
    };
  };
  Phones?: Array<{
    PhoneType?: string;
    PhoneNumber?: string;
    PhoneAreaCode?: string;
    PhoneCountryCode?: string;
  }>;
  Addresses?: Array<{
    AddressType?: string;
    AddressLine1?: string;
    AddressLine2?: string;
    City?: string;
    Region?: string;
    PostalCode?: string;
    Country?: string;
  }>;
  UpdatedDateUTC?: string;
}

function XeroCustomerPanel({
  clientId,
  xeroContactId,
  xeroMetadataRaw,
  onUnlink,
  showSwitch,
  onSwitch,
}: {
  clientId: string;
  xeroContactId: string;
  xeroMetadataRaw: string | null | undefined;
  onUnlink: () => void;
  showSwitch: boolean;
  onSwitch: () => void;
}) {
  const utils = trpc.useUtils();
  const syncMutation = trpc.integrations.syncXeroContact.useMutation({
    onSuccess: () => {
      toast.success("Xero data refreshed.");
      utils.clients.get.invalidate({ id: clientId });
    },
    onError: (err) =>
      toast.error(err.message || "Failed to refresh Xero data."),
  });

  const meta: XeroMeta | null = (() => {
    if (!xeroMetadataRaw) return null;
    try {
      return JSON.parse(xeroMetadataRaw) as XeroMeta;
    } catch {
      return null;
    }
  })();

  // console.log("meta:", meta);

  const outstanding = meta?.Balances?.AccountsReceivable?.Outstanding ?? 0;
  const overdue = meta?.Balances?.AccountsReceivable?.Overdue ?? 0;
  const lastSync = meta?.UpdatedDateUTC
    ? new Date(meta.UpdatedDateUTC).toLocaleDateString()
    : null;

  // console.log("lastSync:", lastSync);

  // Find default or first phone
  const phoneObj =
    meta?.Phones?.find((p) => p.PhoneType === "DEFAULT" && p.PhoneNumber) ||
    meta?.Phones?.find((p) => p.PhoneType === "MOBILE" && p.PhoneNumber) ||
    meta?.Phones?.find((p) => p.PhoneNumber);
  const xeroPhone = phoneObj
    ? [phoneObj.PhoneAreaCode, phoneObj.PhoneNumber].filter(Boolean).join(" ")
    : null;

  // Find default or first address
  const addrObj =
    meta?.Addresses?.find((a) => a.AddressType === "STREET") ||
    meta?.Addresses?.find((a) => a.AddressType === "POBOX") ||
    meta?.Addresses?.[0];
  const billAddr = addrObj
    ? [
        addrObj.AddressLine1,
        addrObj.AddressLine2,
        addrObj.City,
        addrObj.Region,
        addrObj.PostalCode,
        addrObj.Country,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div className="border border-(--border) bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <IntegrationLogo name="Xero" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
            Xero Contact
          </p>
          {meta && (
            <span className="text-[9px] text-muted-foreground font-mono">
              #{xeroContactId.substring(0, 8)}...
            </span>
          )}
        </div>
        <button
          onClick={() => syncMutation.mutate({ clientId })}
          disabled={syncMutation.isPending}
          className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#00b7e5] hover:underline underline-offset-2 disabled:opacity-40"
        >
          {syncMutation.isPending ? (
            <span className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="square"
              strokeLinejoin="miter"
            >
              <path d="M20 5V10H15" />
              <path d="M4 19V14H9" />
              <path d="M20 10C18.5 6.5 15.5 4 12 4C8 4 4.8 6.7 4 10" />
              <path d="M4 14C5.5 17.5 8.5 20 12 20C16 20 19.2 17.3 20 14" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {!meta ? (
        <div className="px-4 py-6 text-center">
          <p className="text-[11px] text-muted-foreground mb-3">
            No Xero data synced yet. Click{" "}
            <span className="font-semibold">Refresh</span> to load this
            contact's Xero details.
          </p>
          <div className="flex items-center justify-center gap-3">
            {showSwitch && (
              <button
                onClick={onSwitch}
                className="text-[10px] font-semibold text-(--brand) hover:underline"
              >
                Switch to QuickBooks
              </button>
            )}
            <button
              onClick={onUnlink}
              className="text-[10px] font-semibold text-(--warn) hover:underline"
            >
              Unlink
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4">
          {/* Balance + currency row */}
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-1 border border-(--border) px-3.5 py-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                Outstanding Balance
              </p>
              <p
                className={`text-[20px] font-bold leading-none tabular-nums ${
                  outstanding > 0 ? "text-(--warn)" : "text-(--green)"
                }`}
              >
                USD{" "}
                {outstanding.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              {overdue > 0 && (
                <p className="text-[10px] text-(--warn) mt-1.5 font-medium">
                  Overdue: USD{" "}
                  {overdue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              )}
            </div>
            <div className="flex-1 border border-(--border) px-3.5 py-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                Xero Contact Name
              </p>
              <p className="text-[13px] font-semibold text-foreground truncate">
                {meta.Name ?? "—"}
              </p>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2">
            {meta.EmailAddress && (
              <div className="border border-(--border) px-3 py-2.5">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Email (Xero)
                </p>
                <p className="text-[11px] text-foreground truncate">
                  {meta.EmailAddress}
                </p>
              </div>
            )}
            {xeroPhone && (
              <div className="border border-(--border) px-3 py-2.5">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Phone (Xero)
                </p>
                <p className="text-[11px] text-foreground">{xeroPhone}</p>
              </div>
            )}
            {billAddr && (
              <div className="border border-(--border) px-3 py-2.5 col-span-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                  Billing Address
                </p>
                <p className="text-[11px] text-foreground">{billAddr}</p>
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-(--border) flex items-center justify-between">
            {lastSync ? (
              <p className="text-[9.5px] text-muted-foreground/50">
                Last updated in Xero: {lastSync}
              </p>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              {showSwitch && (
                <button
                  onClick={onSwitch}
                  className="text-[10px] font-semibold text-(--brand) hover:underline"
                >
                  Switch to QuickBooks
                </button>
              )}
              <button
                onClick={onUnlink}
                className="text-[10px] font-semibold text-(--warn) hover:underline"
              >
                Unlink
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ClientDetailPage() {
  const { clientId } = useParams({ from: "/_app/clients/$clientId" });
  const { tab, page: urlPage, limit: urlLimit } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const utils = trpc.useUtils();

  // ── Per-tab pagination state ───────────────────────────────────────────────
  const activeTab: Tab = tab ?? "overview";

  const [reqPage, setReqPage] = useState(1);
  const [reqLimit, setReqLimit] = useState<PageLimit>(10);

  const [sigPage, setSigPage] = useState(1);
  const [sigLimit, setSigLimit] = useState<PageLimit>(10);

  const [docPage, setDocPage] = useState(1);
  const [docLimit, setDocLimit] = useState<PageLimit>(25);

  const [actPage, setActPage] = useState(1);
  const [actLimit, setActLimit] = useState<PageLimit>(25);

  // ── Queries ───────────────────────────────────────────────────────────────
  const enabled = Boolean(workspaceId && clientId);

  const client_q = trpc.clients.get.useQuery({ id: clientId }, { enabled });
  const clientInsights_q = trpc.clients.insights.useQuery(
    { id: clientId },
    { enabled },
  );
  const requests_q = trpc.requests.list.useQuery({ clientId }, { enabled });
  const signatures_q = trpc.signatures.list.useQuery({ clientId }, { enabled });
  const activity_q = trpc.dashboard.activity.useQuery(
    { clientId, limit: 100 },
    { enabled },
  );
  const documents_q = trpc.documents.list.useQuery({ clientId }, { enabled });
  const messages_q = trpc.messages.list.useQuery({ clientId }, { enabled });

  const isLoading =
    client_q.isLoading ||
    clientInsights_q.isLoading ||
    requests_q.isLoading ||
    signatures_q.isLoading ||
    activity_q.isLoading ||
    documents_q.isLoading ||
    messages_q.isLoading;

  const {
    viewerFile,
    open: openViewer,
    close: closeViewer,
    isOpen: viewerOpen,
  } = useDocumentViewer();

  // Modal states
  const [showDocRequest, setShowDocRequest] = useState(false);
  const [showSigRequest, setShowSigRequest] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showMapper, setShowMapper] = useState(false);
  const [editingClient, setEditingClient] = useState<NonNullable<
    typeof client_q.data
  > | null>(null);
  const [deletingClient, setDeletingClient] = useState<NonNullable<
    typeof client_q.data
  > | null>(null);

  const integrations_q = trpc.integrations.list.useQuery(undefined, {
    enabled: Boolean(workspaceId),
  });

  const batchMap = trpc.integrations.batchMapClients.useMutation({
    onSuccess: () => {
      utils.clients.get.invalidate({ id: clientId });
    },
  });

  const handleUnlinkQbo = async () => {
    try {
      await batchMap.mutateAsync({
        mappings: [{ clientId, qboCustomerId: null }],
      });
      toast.success("QuickBooks mapping removed.");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove QuickBooks mapping.");
    }
  };

  const handleUnlinkXero = async () => {
    try {
      await batchMap.mutateAsync({
        mappings: [{ clientId, xeroContactId: null }],
      });
      toast.success("Xero mapping removed.");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove Xero mapping.");
    }
  };

  const client = client_q.data;
  if (!client && !isLoading) throw new Error("No client found");

  // ── Derived data ──────────────────────────────────────────────────────────
  const allRequests = requests_q.data ?? [];
  const allSignatures = signatures_q.data ?? [];
  const allDocuments = documents_q.data ?? [];
  const allMessages = messages_q.data ?? [];
  const allActivity = activity_q.data ?? [];
  const insights = clientInsights_q.data;

  // client-side pagination slices
  const paginatedRequests = allRequests.slice(
    (reqPage - 1) * reqLimit,
    reqPage * reqLimit,
  );
  const paginatedSignatures = allSignatures.slice(
    (sigPage - 1) * sigLimit,
    sigPage * sigLimit,
  );
  const paginatedDocuments = allDocuments.slice(
    (docPage - 1) * docLimit,
    docPage * docLimit,
  );
  const paginatedActivity = allActivity.slice(
    (actPage - 1) * actLimit,
    actPage * actLimit,
  );

  const recentWork = [
    ...allRequests.slice(0, 3).map((r) => ({
      id: `req-${r.id}`,
      href: {
        to: "/requests/$requestId" as const,
        params: { requestId: r.id },
      },
      title: r.title,
      meta: `${REQUEST_STATUS_LABEL[r.status as RequestStatus]} · ${new Date(r.createdAt).toLocaleDateString()}`,
      badge: REQUEST_STATUS_LABEL[r.status as RequestStatus],
      badgeClass: REQUEST_PILL[r.status as RequestStatus],
    })),
    ...allSignatures.slice(0, 3).map((s) => ({
      id: `sig-${s.id}`,
      href: { to: "/signatures/$" as const, params: { _splat: s.id } },
      title: s.documentName,
      meta: `${SIGNATURE_STATUS_LABEL[s.status as SignatureStatus]} · ${new Date(s.createdAt).toLocaleDateString()}`,
      badge: SIGNATURE_STATUS_LABEL[s.status as SignatureStatus],
      badgeClass: SIGNATURE_PILL[s.status as SignatureStatus],
    })),
  ].slice(0, 5);

  // ── Actions ───────────────────────────────────────────────────────────────
  const copyPortalLink = () => {
    if (!client) return;
    const base =
      runtimeConfig.portalUrl ||
      (import.meta.env.DEV ? "http://localhost:8787" : window.location.origin);
    navigator.clipboard
      .writeText(`${base}/portal/${client.shortCode}`)
      .then(() => toast.success("Portal link copied"))
      .catch(() => toast.error("Failed to copy link"));
  };

  const sendPortalLinkMutation = trpc.clients.sendPortalLink.useMutation({
    onSuccess: () => {
      toast.success("Portal link sent");
      setShowSendConfirm(false);
    },
    onError: (err) => toast.error(err.message || "Failed to send link"),
  });

  const deleteClient = trpc.clients.delete.useMutation({
    onSuccess: async () => {
      toast.success("Client deleted");
      setDeletingClient(null);
      await Promise.all([
        utils.clients.list.invalidate(),
        utils.clients.count.invalidate(),
      ]);
      void navigate({ to: "/clients" });
    },
    onError: (err) => {
      toast.error(err.message || "Failed to delete client");
    },
  });

  const goTab = (t: Tab) =>
    navigate({ search: (prev) => ({ ...prev, tab: t }), replace: true });

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={PAGE_SHELL}>
        <Skeleton className="h-3 w-16" />
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 shrink-0" />
            <div className="space-y-1.5">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-52" />
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-28" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="border border-(--border) bg-card px-3.5 py-3 space-y-2"
            >
              <Skeleton className="h-2 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
        <div className="flex items-end gap-0 border-b border-(--border)">
          {[80, 68, 76, 72, 68, 60].map((w, i) => (
            <Skeleton key={i} className="h-3 mb-3 mx-4" style={{ width: w }} />
          ))}
        </div>
        <div className={CARD_SECTION}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-(--border)">
            <Skeleton className="h-3 w-24" />
          </div>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-3.5 border-b border-(--border)"
            >
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-44" />
                <Skeleton className="h-2.5 w-28" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <ViewerShift isOpen={viewerOpen}>
      <div className={PAGE_SHELL}>
        {/* ── Back ────────────────────────────────────────────────────────── */}
        <Link
          to="/clients"
          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M8 2L4 6l4 4"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </svg>
          All clients
        </Link>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Avatar + name */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-foreground text-background text-[11px] font-bold tracking-wide select-none">
              {client.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-[16px] font-bold tracking-tight text-foreground leading-snug truncate">
                {client.name}
              </h1>
              <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                {client.email}
                <span className="mx-1.5 opacity-30">·</span>
                {client.type === "business" ? "Business" : "Individual"}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button onClick={copyPortalLink} className={GHOST_BTN}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path
                  d="M5 2H2v8h8V7M7 2h3v3"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
                <path
                  d="M5 7l5-5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                />
              </svg>
              Copy link
            </button>
            <button
              onClick={() => setShowSendConfirm(true)}
              className={GHOST_BTN}
            >
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path
                  d="M11 1L1 5.5l4 1.5L7 11l4-10z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </svg>
              Send link
            </button>
            <NewRequestDropdown
              onDocRequest={() => setShowDocRequest(true)}
              onSigRequest={() => setShowSigRequest(true)}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-8 w-8 flex items-center justify-center border border-(--border) bg-card text-muted-foreground hover:text-foreground hover:bg-(--surface-hover) transition-colors">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="3.5" r="1" fill="currentColor" />
                    <circle cx="7" cy="7" r="1" fill="currentColor" />
                    <circle cx="7" cy="10.5" r="1" fill="currentColor" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[140px]">
                <DropdownMenuItem onClick={() => setEditingClient(client)}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setDeletingClient(client)}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* ── Client meta strip ────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            {
              label: "Assigned member",
              value: client.assignedMemberName ?? "—",
            },
            { label: "Phone", value: client.phone ?? "—" },
            {
              label: "Client since",
              value: new Date(client.createdAt).toLocaleDateString(),
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="border border-(--border) bg-card px-3.5 py-3"
            >
              <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                {label}
              </p>
              <p className="text-[12px] font-semibold text-foreground truncate">
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* ── Notes ────────────────────────────────────────────────────────── */}
        {client.notes && (
          <div className="border border-(--border) bg-card px-4 py-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1.5">
              Notes
            </p>
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              {client.notes}
            </p>
          </div>
        )}

        {/* ── QBO Customer Panel ──────────────────────────────────────────── */}
        {client.qboCustomerId && (
          <QboCustomerPanel
            clientId={clientId}
            qboCustomerId={client.qboCustomerId}
            qboMetadataRaw={client.qboMetadata}
            onUnlink={handleUnlinkQbo}
            showSwitch={Boolean(integrations_q.data?.xero?.tenantId)}
            onSwitch={() => setShowMapper(true)}
          />
        )}

        {/* ── Xero Contact Panel ──────────────────────────────────────────── */}
        {client.xeroContactId && (
          <XeroCustomerPanel
            clientId={clientId}
            xeroContactId={client.xeroContactId}
            xeroMetadataRaw={client.xeroMetadata}
            onUnlink={handleUnlinkXero}
            showSwitch={Boolean(integrations_q.data?.qbo?.accountId)}
            onSwitch={() => setShowMapper(true)}
          />
        )}

        {/* ── Unmapped Accounting Section ─────────────────────────────────── */}
        {Boolean(
          integrations_q.data?.qbo?.accountId ||
          integrations_q.data?.xero?.tenantId,
        ) &&
          !client.qboCustomerId &&
          !client.xeroContactId && (
            <div className="border border-(--border) bg-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-foreground mb-1">
                    Accounting Integration
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    This client is not linked to any accounting integration.
                    Link them to QuickBooks or Xero to keep records in sync.
                  </p>
                </div>
                <button
                  onClick={() => setShowMapper(true)}
                  className="h-8 px-3 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity rounded-none cursor-pointer whitespace-nowrap"
                >
                  Link Client
                </button>
              </div>
            </div>
          )}

        {/* ── Tab bar ──────────────────────────────────────────────────────── */}
        <div className="flex items-end border-b border-(--border) overflow-x-auto scrollbar-hide">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => goTab(t.id)}
              className={[
                "relative shrink-0 px-4 h-9 text-[12px] font-medium whitespace-nowrap transition-colors",
                activeTab === t.id
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-(--surface-hover)",
              ].join(" ")}
            >
              {t.label}
              {activeTab === t.id && (
                <span className="absolute bottom-0 inset-x-0 h-[2px] bg-foreground" />
              )}
            </button>
          ))}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Overview
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            {/* Stat row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                {
                  label: "Open requests",
                  value: allRequests.filter((r) => r.status !== "approved")
                    .length,
                },
                {
                  label: "Pending signatures",
                  value: allSignatures.filter((s) => s.status === "pending")
                    .length,
                },
                {
                  label: "Signed",
                  value: allSignatures.filter((s) => s.status === "signed")
                    .length,
                },
                { label: "Documents", value: allDocuments.length },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="border border-(--border) bg-card px-3.5 py-3"
                >
                  <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1">
                    {label}
                  </p>
                  <p className="text-[22px] font-bold text-foreground leading-none">
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {/* Recent work + recent documents — side-by-side on xl */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className={CARD_SECTION}>
                <PanelHeader
                  label="Recent work"
                  action={
                    <button
                      onClick={() => goTab("requests")}
                      className="text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2"
                    >
                      View all
                    </button>
                  }
                />
                <div className="divide-y divide-(--border)">
                  {recentWork.length === 0 ? (
                    <EmptyRow message="No active work yet." />
                  ) : (
                    recentWork.map((item) => (
                      <Link
                        key={item.id}
                        to={item.href.to}
                        params={item.href.params as any}
                        className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-foreground group-hover:text-(--brand) transition-colors truncate">
                            {item.title}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {item.meta}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${item.badgeClass}`}
                        >
                          {item.badge}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              <div className={CARD_SECTION}>
                <PanelHeader
                  label="Recent documents"
                  action={
                    <button
                      onClick={() => goTab("documents")}
                      className="text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2"
                    >
                      View all
                    </button>
                  }
                />
                <div className="divide-y divide-(--border)">
                  {allDocuments.slice(0, 3).length === 0 ? (
                    <EmptyRow message="No documents uploaded yet." />
                  ) : (
                    allDocuments.slice(0, 3).map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-(--border) bg-(--muted-token) text-[8px] font-bold text-muted-foreground">
                          DOC
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-foreground truncate">
                            {doc.fileName}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Uploaded{" "}
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <ViewButton
                          file={{ id: doc.id, fileName: doc.fileName }}
                          onView={openViewer}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Recent messages */}
            <div className={CARD_SECTION}>
              <PanelHeader
                label="Recent messages"
                action={
                  <button
                    onClick={() => goTab("messages")}
                    className="text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2"
                  >
                    View all
                  </button>
                }
              />
              <div className="divide-y divide-(--border)">
                {allMessages.slice(0, 3).length === 0 ? (
                  <EmptyRow message="No messages yet." />
                ) : (
                  allMessages.slice(0, 3).map((msg) => (
                    <div key={msg.id} className="px-4 py-3.5">
                      <p className="text-[10px] text-muted-foreground mb-1">
                        {msg.senderType === "member" ? "You" : client.name}
                        <span className="mx-1.5 opacity-30">·</span>
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </p>
                      <p className="text-[12px] text-foreground leading-relaxed">
                        {msg.body}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Insights
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "insights" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
              <div className={CARD_SECTION}>
                <PanelHeader label="Client health" />
                <div className="p-5">
                  <div className="flex items-end gap-2">
                    <p className="font-display text-[44px] font-bold leading-none text-foreground tabular-nums">
                      {insights?.healthScore ?? 0}
                    </p>
                    <p className="pb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                      / 100
                    </p>
                  </div>
                  <p
                    className={`mt-2 inline-flex px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] ${
                      insights?.healthLabel === "healthy"
                        ? "bg-(--green-bg) text-(--green)"
                        : insights?.healthLabel === "watch"
                          ? "bg-(--accent-bg) text-(--accent)"
                          : "bg-(--colors-red-100) text-(--warn)"
                    }`}
                  >
                    {insights?.healthLabel === "healthy"
                      ? "Healthy"
                      : insights?.healthLabel === "watch"
                        ? "Watch"
                        : "At risk"}
                  </p>
                  <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
                    Score is derived from actual upload completeness, request
                    completion, signature completion, response time, recent
                    activity, and overdue work.
                  </p>
                </div>
              </div>

              <div className={CARD_SECTION}>
                <PanelHeader label="Engagement metrics" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-(--border)">
                  {[
                    {
                      label: "Upload completeness",
                      value: `${insights?.uploadCompleteness ?? 0}%`,
                    },
                    {
                      label: "Request completion",
                      value: `${insights?.requestCompletionRate ?? 0}%`,
                    },
                    {
                      label: "Signature completion",
                      value: `${insights?.signatureCompletionRate ?? 0}%`,
                    },
                    {
                      label: "Avg response",
                      value:
                        insights?.avgResponseDays == null
                          ? "—"
                          : `${insights.avgResponseDays}d`,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="border-r last:border-r-0 border-(--border) px-4 py-3"
                    >
                      <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                        {item.label}
                      </p>
                      <p className="mt-1.5 text-[20px] font-bold text-foreground leading-none tabular-nums">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
                  {[
                    {
                      label: "Open requests",
                      value: insights?.openRequests ?? 0,
                    },
                    { label: "Overdue", value: insights?.overdueRequests ?? 0 },
                    {
                      label: "Client uploads",
                      value: insights?.clientUploads ?? 0,
                    },
                    {
                      label: "Data held",
                      value: formatBytes(insights?.storageBytes ?? 0),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="border-r last:border-r-0 border-(--border) px-4 py-3"
                    >
                      <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                        {item.label}
                      </p>
                      <p className="mt-1.5 text-[16px] font-bold text-foreground leading-none tabular-nums">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className={CARD_SECTION}>
                <PanelHeader label="Deadline posture" />
                <div className="divide-y divide-(--border)">
                  {(insights?.upcomingDeadlines ?? []).length === 0 ? (
                    <EmptyRow message="No active deadlines for this client." />
                  ) : (
                    insights?.upcomingDeadlines.map((deadline) => (
                      <Link
                        key={deadline.id}
                        to="/requests/$requestId"
                        params={{ requestId: deadline.id }}
                        className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-foreground group-hover:text-(--brand) truncate">
                            {deadline.title}
                          </p>
                          <p className="mt-0.5 text-[10px] text-muted-foreground">
                            Due{" "}
                            {deadline.dueDate
                              ? new Date(deadline.dueDate).toLocaleDateString()
                              : "—"}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${deadline.daysUntilDue < 0 ? "bg-(--colors-red-100) text-(--warn)" : deadline.daysUntilDue <= 3 ? "bg-(--accent-bg) text-(--accent)" : "bg-(--muted-token) text-muted-foreground"}`}
                        >
                          {deadline.daysUntilDue < 0
                            ? `${Math.abs(deadline.daysUntilDue)}d overdue`
                            : deadline.daysUntilDue === 0
                              ? "Due today"
                              : `${deadline.daysUntilDue}d left`}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              <div className={CARD_SECTION}>
                <PanelHeader label="Client value signal" />
                <div className="p-4 space-y-3">
                  <div className="border border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) px-3.5 py-3">
                    <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                      Work-volume signal
                    </p>
                    <p className="mt-1 text-[14px] font-bold capitalize text-foreground">
                      {insights?.lifetimeValueSignal ?? "early"}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground leading-relaxed">
                      Based on completed requests, signed documents, and client
                      uploads. This is not revenue; it is a practical
                      workload/relationship signal.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      {
                        label: "Total work",
                        value: insights?.totalWorkVolume ?? 0,
                      },
                      {
                        label: "Pending reminders",
                        value: insights?.pendingReminderCount ?? 0,
                      },
                      {
                        label: "Completed requests",
                        value: insights?.completedRequests ?? 0,
                      },
                      {
                        label: "Signed",
                        value: `${insights?.signedSignatures ?? 0}/${insights?.totalSignatures ?? 0}`,
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="border border-(--border) px-3 py-2.5"
                      >
                        <p className="text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                          {item.label}
                        </p>
                        <p className="mt-1 text-[16px] font-bold text-foreground tabular-nums">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Requests
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "requests" && (
          <div className={CARD_SECTION}>
            <PanelHeader
              label={`Document requests ${allRequests.length > 0 ? `(${allRequests.length})` : ""}`}
              action={
                <button
                  onClick={() => setShowDocRequest(true)}
                  className="text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2"
                >
                  + New request
                </button>
              }
            />
            <div className="divide-y divide-(--border)">
              {paginatedRequests.length === 0 ? (
                <EmptyRow message="No document requests yet." />
              ) : (
                paginatedRequests.map((r) => (
                  <Link
                    key={r.id}
                    to="/requests/$requestId"
                    params={{ requestId: r.id }}
                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground group-hover:text-(--brand) transition-colors truncate">
                        {r.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Sent {new Date(r.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${REQUEST_PILL[r.status as RequestStatus]}`}
                    >
                      {REQUEST_STATUS_LABEL[r.status as RequestStatus]}
                    </span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className="shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors"
                    >
                      <path
                        d="M4 2l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="square"
                        strokeLinejoin="miter"
                      />
                    </svg>
                  </Link>
                ))
              )}
            </div>
            {allRequests.length > PAGE_LIMITS[0] && (
              <Pagination
                page={reqPage}
                limit={reqLimit}
                total={allRequests.length}
                onPageChange={setReqPage}
                onLimitChange={setReqLimit}
              />
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Signatures
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "signatures" && (
          <div className={CARD_SECTION}>
            <PanelHeader
              label={`Signatures ${allSignatures.length > 0 ? `(${allSignatures.length})` : ""}`}
              action={
                <button
                  onClick={() => setShowSigRequest(true)}
                  className="text-[10px] font-semibold text-(--brand) hover:underline underline-offset-2"
                >
                  + Request signature
                </button>
              }
            />
            <div className="divide-y divide-(--border)">
              {paginatedSignatures.length === 0 ? (
                <EmptyRow message="No signature requests yet." />
              ) : (
                paginatedSignatures.map((sig) => (
                  <Link
                    key={sig.id}
                    to="/signatures/$"
                    params={{ _splat: sig.id }}
                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors group"
                  >
                    {/* Status indicator dot */}
                    <div
                      className={[
                        "h-2 w-2 shrink-0 rounded-full",
                        sig.status === "signed"
                          ? "bg-(--green)"
                          : sig.status === "declined"
                            ? "bg-(--warn)"
                            : "bg-(--muted)",
                      ].join(" ")}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground group-hover:text-(--brand) transition-colors truncate">
                        {sig.documentName}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {sig.status === "signed"
                          ? `Signed ${sig.signedAt ? new Date(sig.signedAt).toLocaleDateString() : "—"}`
                          : `Requested ${new Date(sig.createdAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${SIGNATURE_PILL[sig.status as SignatureStatus]}`}
                    >
                      {SIGNATURE_STATUS_LABEL[sig.status as SignatureStatus]}
                    </span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className="shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors"
                    >
                      <path
                        d="M4 2l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="square"
                        strokeLinejoin="miter"
                      />
                    </svg>
                  </Link>
                ))
              )}
            </div>
            {allSignatures.length > PAGE_LIMITS[0] && (
              <Pagination
                page={sigPage}
                limit={sigLimit}
                total={allSignatures.length}
                onPageChange={setSigPage}
                onLimitChange={setSigLimit}
              />
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Documents
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "documents" && (
          <div className={CARD_SECTION}>
            <PanelHeader
              label={`Documents ${allDocuments.length > 0 ? `(${allDocuments.length})` : ""}`}
            />
            <div className="divide-y divide-(--border)">
              {paginatedDocuments.length === 0 ? (
                <EmptyRow message="No documents uploaded yet." />
              ) : (
                paginatedDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-(--surface-hover) transition-colors"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-(--border) bg-(--muted-token) text-[8px] font-bold text-muted-foreground">
                      DOC
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground truncate">
                        {doc.fileName}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <ViewButton
                      file={{ id: doc.id, fileName: doc.fileName }}
                      onView={openViewer}
                    />
                  </div>
                ))
              )}
            </div>
            {allDocuments.length > PAGE_LIMITS[0] && (
              <Pagination
                page={docPage}
                limit={docLimit}
                total={allDocuments.length}
                onPageChange={setDocPage}
                onLimitChange={setDocLimit}
              />
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Messages
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "messages" && (
          <div className={CARD_SECTION}>
            <PanelHeader label="Messages" />
            <div className="px-4 py-4 space-y-4 max-h-[520px] overflow-y-auto bg-(--bg)">
              {allMessages.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No messages yet.
                </p>
              ) : (
                allMessages.map((msg) => {
                  const isYou = msg.senderType === "member";
                  const name = isYou ? "You" : client.name;
                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-2.5 ${isYou ? "flex-row-reverse" : ""}`}
                    >
                      <div
                        className={`flex h-6 w-6 shrink-0 items-center justify-center text-[8px] font-bold mt-0.5 ${isYou ? "bg-foreground text-background" : "bg-(--border) text-muted-foreground"}`}
                      >
                        {name.slice(0, 2).toUpperCase()}
                      </div>
                      <div
                        className={`flex flex-col gap-1 max-w-[82%] ${isYou ? "items-end" : "items-start"}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <p className="text-[9.5px] text-muted-foreground font-medium">
                            {name}
                          </p>
                          <span className="text-muted-foreground/30 text-[9px]">
                            ·
                          </span>
                          <p className="text-[9px] text-muted-foreground/50">
                            {new Date(msg.createdAt).toLocaleTimeString()}
                          </p>
                        </div>
                        <div
                          className={`px-3 py-2 text-[12px] leading-relaxed border ${isYou ? "bg-foreground text-background border-foreground" : "bg-card border-(--border) text-foreground"}`}
                        >
                          {msg.body}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="px-4 py-3 border-t border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
              <div className="flex items-start gap-2.5">
                <div className="h-5 w-5 shrink-0 flex items-center justify-center mt-0.5 bg-(--brand-dim)">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <circle
                      cx="6"
                      cy="6"
                      r="5"
                      stroke="var(--brand)"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M6 3.5v3M6 8.5h.01"
                      stroke="var(--brand)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  To reply, open the relevant item from the{" "}
                  <button
                    onClick={() => goTab("requests")}
                    className="text-(--brand) font-semibold hover:underline"
                  >
                    Requests tab
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
                Tab: Activity
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "activity" && (
          <div className={CARD_SECTION}>
            <PanelHeader label="Activity log" />
            {paginatedActivity.length === 0 ? (
              <EmptyRow message="No activity recorded yet." />
            ) : (
              <div className="p-4 space-y-3">
                {paginatedActivity.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div
                      className={`flex h-6 w-6 shrink-0 items-center justify-center border mt-0.5 ${ACTIVITY_BG[item.type] ?? "bg-(--muted-token) border-(--border)"}`}
                    >
                      <ActivityIcon type={item.type} />
                    </div>
                    <p className="flex-1 min-w-0 text-[11px] text-muted-foreground leading-relaxed pt-0.5">
                      {item.payload}
                    </p>
                    <span className="text-[9.5px] text-muted-foreground/40 shrink-0 whitespace-nowrap pt-0.5">
                      {new Date(item.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {allActivity.length > PAGE_LIMITS[0] && (
              <Pagination
                page={actPage}
                limit={actLimit}
                total={allActivity.length}
                onPageChange={setActPage}
                onLimitChange={setActLimit}
              />
            )}
          </div>
        )}

        {/* ── Modals ────────────────────────────────────────────────────────── */}
        <NewRequestModal
          open={showDocRequest}
          onClose={() => setShowDocRequest(false)}
          prefilledClientId={clientId}
          clientPhone={client?.phone ?? undefined}
        />
        <NewSignatureModal
          open={showSigRequest}
          onClose={() => setShowSigRequest(false)}
          prefilledClientId={clientId}
          clientPhone={client?.phone ?? undefined}
        />
        <EditClientModal
          open={!!editingClient}
          onClose={() => setEditingClient(null)}
          client={editingClient}
        />
        <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send portal link?</AlertDialogTitle>
              <AlertDialogDescription>
                We'll send a secure magic link to{" "}
                <strong>{client?.name}</strong> using your workspace's default
                communication method.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => sendPortalLinkMutation.mutate({ id: clientId })}
              >
                Send link
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog
          open={!!deletingClient}
          onOpenChange={(open) => !open && setDeletingClient(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete client?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {deletingClient?.name}? This
                will also delete all associated requests and data. This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (deletingClient)
                    deleteClient.mutate({ id: deletingClient.id });
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <DocumentViewer file={viewerFile} onClose={closeViewer} />
      <ClientIntegrationMapper
        open={showMapper}
        onOpenChange={setShowMapper}
        mode="single"
        clientId={clientId}
        clientName={client?.name}
        onSaved={() => utils.clients.get.invalidate({ id: clientId })}
      />
    </ViewerShift>
  );
}
